import { useState } from 'react';
import { AlertTriangle, Check, KeyRound, Loader2, MapPin, Plug, Plus, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import {
  useEslAdminStores,
  useEslTenantConfig,
  useSaveEslStore,
  useSaveEslTenantConfig,
  useTestEslConnection,
  useProvisionEslStore,
} from '@/hooks/use-esl';

// Super-admin only. Two things a business cannot set up for itself:
//
//  1. The label-platform account. It is ours, not theirs — an external-POS or
//     standalone client must never hold it, which is the whole reason this
//     service exists rather than handing out vendor logins. Normally that is
//     the shared account in the service's own secrets and there is nothing to
//     enter; this form exists for the business that brings its own.
//  2. Which vendor store each of their locations maps to. That id comes from
//     the vendor account they have no access to.
//
// The credentials are WRITE-ONLY. There is no endpoint that returns them, so
// this form can only ever replace them, never show them. "Test connection" is
// the only honest way to verify a secret nobody can read: use it.

interface EslConnectionCardProps {
  tenantId: string;
  locations: Array<{ id: string; name: string; address?: string | null }>;
}

export function EslConnectionCard({ tenantId, locations }: EslConnectionCardProps) {
  const { data: status, isLoading } = useEslTenantConfig(tenantId);
  const { data: stores } = useEslAdminStores(tenantId);
  const save = useSaveEslTenantConfig(tenantId);
  const test = useTestEslConnection(tenantId);
  const saveStore = useSaveEslStore(tenantId);
  const provision = useProvisionEslStore(tenantId);

  const [editing, setEditing] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  const configured = status?.configured === true;
  const source = status?.credentialSource ?? 'none';
  const onPlatformAccount = source === 'platform';

  const beginEdit = () => {
    setBaseUrl(status?.baseUrl ?? 'https://hub.minewtag.com/apis');
    setUsername('');
    setPassword('');
    setEditing(true);
  };

  const onSave = async () => {
    // Both or neither — the API enforces this too. Neither means "keep the
    // stored credentials and just change the host".
    const hasUser = !!username.trim();
    const hasPass = !!password.trim();
    if (hasUser !== hasPass) {
      toast.error('Enter both username and password, or leave both blank to keep the stored ones');
      return;
    }
    // `configured` is true on the shared platform account too, so it cannot be
    // the test here: switching a business TO its own account still needs both
    // fields, even though the tenant was already working a moment ago.
    if (source !== 'tenant' && !hasUser) {
      toast.error('Username and password are required to give this business its own account');
      return;
    }
    try {
      const res = await save.mutateAsync({
        provider: 'minew',
        baseUrl: baseUrl.trim() || undefined,
        ...(hasUser ? { username: username.trim(), password: password.trim() } : {}),
      });
      toast.success(res.credentialsChanged ? 'Credentials saved' : 'Connection settings saved');
      setUsername('');
      setPassword('');
      setEditing(false);
      setTestResult(null);
    } catch (e) {
      const msg = (e as { error?: { message?: string } })?.error?.message;
      toast.error(msg || 'Could not save');
    }
  };

  const onTest = async () => {
    setTestResult(null);
    const res = await test.mutateAsync();
    setTestResult(res);
    if (res.ok) toast.success('Connected');
    else toast.error('Connection failed');
  };

  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="mb-1 flex items-center gap-2">
        <Plug className="h-4 w-4 text-primary" />
        <h3 className="font-medium">Shelf Labels connection</h3>
        {!configured ? (
          <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">Not configured</span>
        ) : onPlatformAccount ? (
          <span className="rounded bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">Platform account</span>
        ) : (
          <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">Own account</span>
        )}
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Most businesses run on our shared label-platform account, which is held in the service's own secrets — nothing
        to enter here. Only set credentials below if this business brings its own account. Either way the business never
        sees them, and they are never shown again after saving.
      </p>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {/* In dev without an encryption key the credentials are stored as
              plaintext. An operator should be told that rather than assume. */}
          {configured && !onPlatformAccount && status?.encryptionConfigured === false && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Stored without encryption — <code>PROVIDER_CREDS_ENC_KEY</code> is not set on this environment. The API
                refuses to store credentials this way in production.
              </span>
            </div>
          )}

          {!editing ? (
            <div className="space-y-3">
              {configured && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">Host</dt>
                  <dd className="font-mono text-xs">{status?.baseUrl}</dd>
                  <dt className="text-muted-foreground">Credentials</dt>
                  <dd className="flex items-center gap-1 text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {onPlatformAccount
                      ? 'shared platform account (from the service secrets)'
                      : 'stored, not retrievable'}
                  </dd>
                  <dt className="text-muted-foreground">Last updated</dt>
                  <dd>{status?.updatedAt ? new Date(status.updatedAt).toLocaleString() : '—'}</dd>
                </dl>
              )}

              {testResult && (
                <div
                  className={`flex items-start gap-2 rounded-md p-3 text-xs ${
                    testResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {testResult.ok ? (
                    <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  <span>{testResult.ok ? 'Signed in to the label platform.' : testResult.error}</span>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={beginEdit}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white"
                >
                  <KeyRound className="h-4 w-4" />
                  {onPlatformAccount
                    ? 'Use a separate account'
                    : configured
                      ? 'Replace credentials'
                      : 'Set up connection'}
                </button>
                {configured && (
                  <button
                    onClick={onTest}
                    disabled={test.isPending}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50"
                  >
                    {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                    Test connection
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Platform host</label>
                <input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  className="w-full rounded-md border border-border p-2 font-mono text-xs"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Username</label>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="off"
                    className="w-full rounded-md border border-border p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="w-full rounded-md border border-border p-2 text-sm"
                  />
                </div>
              </div>
              {configured && (
                <p className="text-xs text-muted-foreground">
                  Leave both blank to change only the host — the stored credentials are kept.
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={onSave}
                  disabled={save.isPending}
                  className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setUsername('');
                    setPassword('');
                  }}
                  className="rounded-md border border-border px-3 py-2 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Location -> vendor store. Nothing in the portal works without this:
              gateways, labels and templates are all per store. */}
          {configured && (
            <div className="mt-6 border-t border-border pt-4">
              <div className="mb-1 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium">Stores</h4>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Each location needs a store on the label platform before its gateways or labels can be managed. Create
                one here — nobody needs to log into the platform, and a business only ever sees its own store.
              </p>
              <div className="space-y-2">
                {locations.map((loc) => (
                  <StoreRow
                    key={loc.id}
                    location={loc}
                    current={stores?.find((s) => s.locationId === loc.id)?.storeRef ?? ''}
                    creating={provision.isPending}
                    onCreate={async () => {
                      try {
                        const res = await provision.mutateAsync({
                          locationId: loc.id,
                          name: loc.name,
                          address: loc.address || loc.name,
                        });
                        // The platform may already hold a store for this
                        // location from a previous setup; we adopt it rather
                        // than creating a duplicate. Say which happened —
                        // "created" would be a lie, and the distinction matters
                        // if tags are already bound to it.
                        toast.success(
                          res.adopted
                            ? `Linked ${loc.name} to its existing store (${res.storeRef})`
                            : `Store created for ${loc.name} (${res.storeRef})`,
                        );
                      } catch (e) {
                        const msg = (e as { error?: { message?: string } })?.error?.message;
                        toast.error(msg || 'Could not create the store');
                      }
                    }}
                    onSave={async (storeRef) => {
                      try {
                        await saveStore.mutateAsync({ locationId: loc.id, storeRef });
                        toast.success(`${loc.name} linked`);
                      } catch (e) {
                        const msg = (e as { error?: { message?: string } })?.error?.message;
                        toast.error(msg || 'Could not link this location');
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StoreRow({
  location,
  current,
  creating,
  onCreate,
  onSave,
}: {
  location: { id: string; name: string; address?: string | null };
  current: string;
  creating: boolean;
  onCreate: () => void | Promise<void>;
  onSave: (storeRef: string) => void | Promise<void>;
}) {
  const [value, setValue] = useState(current);
  // Typing an existing id is the exception, not the flow — it is only for a
  // location whose store already exists on the platform from before.
  const [linkingManually, setLinkingManually] = useState(false);
  const dirty = value.trim() !== current;

  if (!current && !linkingManually) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2">
        <div>
          <div className="text-sm font-medium">{location.name}</div>
          <div className="text-[11px] text-muted-foreground">{location.address || 'No address on file'}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={creating}
            onClick={onCreate}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Create store
          </button>
          <button
            onClick={() => setLinkingManually(true)}
            className="text-[11px] text-muted-foreground underline hover:text-foreground"
          >
            link an existing one
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
      <span className="min-w-[140px] text-sm font-medium">{location.name}</span>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Existing store id on the label platform"
        className="flex-1 rounded-md border border-border p-1.5 font-mono text-xs"
      />
      <button
        disabled={!dirty || !value.trim()}
        onClick={() => onSave(value.trim())}
        className="rounded-md border border-border px-2 py-1 text-xs disabled:opacity-40"
      >
        {current ? 'Update' : 'Link'}
      </button>
    </div>
  );
}
